import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  tap,
  switchMap,
  mergeMap,
} from "rxjs/operators";

import * as FuelStocksActionTypes from "../../actions/fuelStocks/fuelStocks.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { FuelStocksService } from "src/app/shared/services/fuelStock/fuelStock.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { DeliveriesService } from "src/app/shared/services/delivery/delivery.service";

@Injectable()
export class FuelStocksEffects {
  getFuelStocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getFuelStocks),
      switchMap((action) =>
        this.fuelStockService
          .getFuelStocks(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              FuelStocksActionTypes.loadFuelStocks({ response: result })
            ),
            catchError((error) => {
              const actions = [
                FuelStocksActionTypes.loadFuelStocksLoadEnd(),
                FuelStocksActionTypes.loadFuelStocks({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "FuelStockManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getFuelStockMovements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getFuelStockMovements),
      switchMap((action) =>
        this.deliveryService
          .getFuelStockMovements(
            action.options?.paging,
            action.options?.filteringArgs,
            action.options?.sortingArgs,
            action.options?.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              FuelStocksActionTypes.loadFuelStockMovements({ response: result })
            ),
            catchError((error) => {
              const actions = [
                FuelStocksActionTypes.loadFuelStocksLoadEnd(),
                FuelStocksActionTypes.loadFuelStockMovements({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "FuelStockManagement.loadErrorFuelStockMovements",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getFuelStockExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getFuelStockExcelList),
      exhaustMap((action) =>
        this.fuelStockService
          .getFuelStocks(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              FuelStocksActionTypes.loadFuelStockExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                FuelStocksActionTypes.loadFuelStockExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "FuelStockManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Fuel Stocks Table Config
  getFuelStockTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getFuelStockTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              FuelStocksActionTypes.loadFuelStockTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                FuelStocksActionTypes.loadFuelStockTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getFuelStockMovementsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getFuelStockMovementsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              FuelStocksActionTypes.loadFuelStockMovementsTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                FuelStocksActionTypes.loadFuelStockMovementsTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Fuel Stocks Table Config
  saveFuelStockTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.saveFuelStockTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            FuelStocksActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              FuelStocksActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Save Fuel Stocks Movements Table Config
  saveFuelStockMovementsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.saveFuelStockMovementsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            FuelStocksActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              FuelStocksActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateFuelStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.updateFuelStock),
      exhaustMap((action) =>
        this.fuelStockService.updateFuelStock(action).pipe(
          map((result) => {
            return FuelStocksActionTypes.getFuelStock({
              fuelStock: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "FuelStockManagement.fuelStockUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              FuelStocksActionTypes.loadFuelStocksLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "FuelStockManagement.FuelStockFailedUpdate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getFuelStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getFuelStock),
      exhaustMap((action) =>
        this.fuelStockService.getFuelStock(action).pipe(
          map((result) =>
            FuelStocksActionTypes.setFuelStock({ response: result })
          ),
          catchError((error) => {
            const actions = [
              FuelStocksActionTypes.loadFuelStocksLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "FuelStockManagement.errorLoadFuelStock",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getLastMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getLastMeasurements),
      exhaustMap((action) =>
        this.fuelStockService.getMovements(action).pipe(
          map((result) =>
            FuelStocksActionTypes.setLastMeasurements({ result: result })
          ),
          catchError((error) => {
            const actions = [
              FuelStocksActionTypes.loadFuelStocksLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "FuelStockManagement.failedGetMeasurement",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getLastDeliveries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getLastDeliveries),
      exhaustMap((action) =>
        this.fuelStockService.getMovements(action).pipe(
          map((result) =>
            FuelStocksActionTypes.setLastDeliveries({ result: result })
          ),
          catchError((error) => {
            const actions = [
              FuelStocksActionTypes.loadFuelStocksLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "FuelStockManagement.failedGetMeasurement",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createFuelStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.createFuelStock),
      exhaustMap((action) =>
        this.fuelStockService.createFuelStock(action).pipe(
          mergeMap((result) => [
            FuelStocksActionTypes.resetCreation(),
            FuelStocksActionTypes.getFuelStock({
              fuelStock: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "FuelStockManagement.FuelStockCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              FuelStocksActionTypes.loadFuelStocksLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "FuelStockManagement.FuelStockFailedCreate",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getInventoryStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelStocksActionTypes.getInventoryStats),
      exhaustMap((action) =>
        this.fuelStockService.getInventoryStats(action).pipe(
          map((result) =>
            FuelStocksActionTypes.loadInventoryStats({ result: result })
          ),
          catchError((error) => {
            const actions = [
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "FuelStockManagement.failedGetInventoryStats",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private fuelStockService: FuelStocksService,
    private deliveryService: DeliveriesService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
